/**
 * @sunday/cart — the one basket. The Shopify cart is the only container; a bed is just a
 * grouping key on cart lines. No DOM here beyond one event.
 *
 * Line properties (handbook, "Cart and beds"):
 *   _bed          hidden   groups a mattress with its pillow, topper and protector  (bed1, our, par, kidr…)
 *   _bed_label    hidden   group heading in the cart, only when the buyer renamed the bed ("Parents' room"); else the name comes from _bed
 *   _from_builder hidden   lines Build your bed may replace on "Update cart"
 *   Custom Size   visible  "74×58″ (length × width)" (older orders: "58″ × 74″ (width × length)"); the variant is the smallest standard size containing it
 *
 * Which products are mattresses comes from the store (sunday_mattress entries), passed in by
 * the theme as window.Sunday.mattressProductIds. Nothing is named in code.
 *
 * Every change dispatches `sunday:cart` on document with { cart, added? }.
 */

import { t as T } from '@sunday/text';
import * as state from '@sunday/state';

const root = () => (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';

async function call(path, body) {
  const res = await fetch(root() + path, {
    method: body ? 'POST' : 'GET',
    headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
    body: body ? JSON.stringify(body) : undefined,
  });
  const data = await res.json().catch(() => ({}));
  if (!res.ok) {
    const err = new Error(data.description || data.message || T('common.failed'));
    err.status = res.status;
    throw err;
  }
  return data;
}

function announce(cart, extra = {}) {
  document.dispatchEvent(new CustomEvent('sunday:cart', { detail: { cart, ...extra } }));
  return cart;
}

const mattressIds = () => ((window.Sunday && window.Sunday.mattressProductIds) || []).map(Number);

export const isMattress = (line) => mattressIds().includes(Number(line.product_id));
export const bedOf = (line) => (line.properties && line.properties._bed) || null;

/**
 * The cart, read fresh. 25 Oct (speed): a plain GET /cart.js (about 0.2 s). It used to be a no-op POST to
 * cart/update.js, which Shopify treats as a change (about 0.65 s: the store waits on an outside service on every
 * change). The POST stays only as the fallback when the GET fails (a preview domain that redirects it).
 */
export async function get() {
  try {
    return await call('cart.js');
  } catch (e) {
    return call('cart/update.js', { updates: {} });
  }
}

/* Liquid writes line properties as [key, value] pairs; the cart API as an object. */
const props = (p) => (Array.isArray(p) ? Object.fromEntries(p) : p || {});

let here = null;
/**
 * The cart as this page was built with it (snippets/sunday-head.liquid prints it), so the buttons that follow the
 * cart need no call when a page opens. A page the browser brings back from memory (Back) reads it fresh.
 * Every module that asks shares one answer.
 */
export function current() {
  if (here) return here;
  const back = (performance.getEntriesByType && performance.getEntriesByType('navigation')[0]?.type) === 'back_forward';
  let c = null;
  if (!back) {
    try {
      c = JSON.parse(document.querySelector('script[data-sunday-cart]').textContent);
      c.items = (c.items || []).map((l) => ({ ...l, properties: props(l.properties) }));
    } catch (e) {
      c = null;
    }
  }
  here = c ? Promise.resolve(c) : get();
  return here;
}
document.addEventListener('sunday:cart', (e) => {
  if (e.detail && e.detail.cart && e.detail.cart.items) here = Promise.resolve(e.detail.cart);
});
addEventListener('pageshow', (e) => {
  if (e.persisted) here = null;
});
/* 25 Oct (speed): the cart can change elsewhere only while this page is out of sight (another tab, another app).
   Coming back, it is read once in the background, so an Add right after still starts from the true cart. */
let away = document.visibilityState === 'hidden'; /* opened in a background tab */
document.addEventListener('visibilitychange', () => {
  if (document.visibilityState === 'hidden') away = true;
  else if (away) {
    away = false;
    const was = here;
    here = get();
    here
      .then(async (c) => {
        const b = was && (await was.catch(() => null));
        if (b && sig(b) !== sig(c)) announce(c); /* the buttons and the count follow what changed elsewhere */
      })
      .catch(() => (here = null));
  }
});
const sig = (c) => `${c.item_count}|${c.total_price}|${(c.items || []).map((l) => l.key + ':' + l.quantity).join(',')}`;

/** Lines grouped by bed, in the order beds first appear. Lines without a bed go to "extras". */
export function beds(cart) {
  const out = new Map();
  for (const line of cart.items) {
    const key = bedOf(line) || 'extras';
    if (!out.has(key)) out.set(key, { key, label: (line.properties && line.properties._bed_label) || '', lines: [] });
    out.get(key).lines.push(line);
  }
  return [...out.values()];
}

/** A fresh bed key: bed1, bed2… not used yet in this cart. */
export function newBedKey(cart) {
  const used = new Set(cart.items.map(bedOf).filter(Boolean));
  /* 26 Oct (AR go): a new bed always takes the next number (never a freed one), so it shows last in the cart */
  let n = 1;
  for (const k of used) { const m = /^bed(\d+)$/.exec(k); if (m) n = Math.max(n, Number(m[1]) + 1); }
  while (used.has('bed' + n)) n++;
  return 'bed' + n;
}

/**
 * 25 Oct (AR's go, checkout mockup): the line every mattress carries into checkout's order summary, on every step.
 * 25 Oct (AR): no delivery days in it ("Arrives: Not compressed · 100 nights to try"); the days came from the pincode
 * checked on the site and could disagree with checkout's own dates or go stale. Nights from store facts; words from
 * the language file.
 */
/* 26 Oct (AR go): in English in every language, so the order reads the same for the team (header setting
   "Order notes in English", on <header data-sd-arr-k / data-sd-arr-v>); the language file only if those are empty. */
const hdr = () => document.querySelector('[data-sd-step]');
export function arriveKey() {
  const h = hdr();
  return (h && h.dataset.sdArrK) || T('cart.note_arrives');
}
export function arriveNote() {
  const h = hdr();
  const n = (window.Sunday && window.Sunday.trialNights) || '';
  return h && h.dataset.sdArrV ? h.dataset.sdArrV.replace('{n}', n) : T('cart.note_arrives_v', { n });
}
export function mattressNotes() {
  return { [arriveKey()]: arriveNote() };
}

/**
 * The order notes for the team (Additional details), one rule for the cart page and every Add:
 * Help me choose's notes, Language = the site language read (English name, header data-sd-lang), Journey = the pages
 * seen (localStorage['sunday-journey'], kept by assets/sunday-header.js), short: "Home › Mattresses › OP › Cart ›
 * Checkout". A new visit after 30 minutes or more starts after "‖ +2d ‖" (+45m, +3h, +2d).
 * atCart: the line as the cart page will see it, with the cart page added the way the header adds a page. 25 Oct (speed):
 * every Add carries these notes (Shopify takes them in the same call, at no cost), so a buyer who goes from Add to
 * the cart finds them saved, and the cart page sends nothing (a separate save is a cart change, about 0.7 s).
 */
export function journeyLine(steps, end) {
  if (!steps || !steps.length) return null;
  const out = [];
  let prev = null;
  for (const x of steps) {
    if (prev && x.t - prev.t >= 18e5) {
      const m = Math.round((x.t - prev.t) / 6e4);
      out.push('‖ +' + (m < 60 ? m + 'm' : m < 1440 ? Math.round(m / 60) + 'h' : Math.round(m / 1440) + 'd') + ' ‖');
    }
    const lang = (!prev && x.c && !/^en/.test(x.c)) || (prev && x.l !== prev.l) ? '[' + x.l + '] ' : '';
    out.push(lang + x.p);
    prev = x;
  }
  /* 24 Oct (AR: "order notes should be short"): the last 12 steps only, each page once in a row.
     26 Oct (AR go): back and forth between two pages (Cart › Pay & delivery › Cart › Pay & delivery) shows once. */
  const short = [];
  for (const x of out) {
    const n = short.length;
    if (x === short[n - 1]) continue;
    if (n >= 3 && x === short[n - 2] && short[n - 1] === short[n - 3]) { short.pop(); continue; }
    short.push(x);
  }
  const keep = short.length > 12 ? ['…', ...short.slice(-12)] : short;
  if (end) keep.push(end);
  return keep.join(' › ').replace(/ › ‖/g, ' ‖').replace(/‖ › /g, '‖ ').replace(/^… ‖ /, '… ');
}

export function orderNotes(atCart = false) {
  const h = document.querySelector('[data-sd-step]');
  const out = { ...(state.get('attrs') || {}) };
  if (h && h.dataset.sdLang) out.Language = h.dataset.sdLang;
  let j = null;
  try { j = JSON.parse(localStorage.getItem('sunday-journey') || 'null'); } catch (e) { return out; }
  let steps = j && Array.isArray(j.s) ? j.s.map((x) => ({ ...x })) : [];
  if (atCart && h && h.dataset.sdJcart) {
    const tok = decodeURIComponent((document.cookie.match(/(?:^|; )cart=([^;]*)/) || [])[1] || '').split('?')[0];
    if (j && j.sent && tok && j.sent !== tok) steps = steps.filter((x) => x.t > (j.at || 0));
    const now = Date.now();
    const last = steps[steps.length - 1];
    if (last && last.p === h.dataset.sdJcart && last.l === h.dataset.sdLang && now - last.t < 18e5) last.t = now;
    else steps.push({ p: h.dataset.sdJcart, l: h.dataset.sdLang, c: document.documentElement.lang, t: now });
  }
  const line = journeyLine(steps.slice(-80), (h && h.dataset.sdJco) || '');
  if (line) out.Journey = line;
  return out;
}

/** The custom-size line property value, width first. */
/* 25 Oct (AR go): length × width, as the site shows sizes (78×60″); orders before this read "W″ × L″ (width × length)" */
export const customSizeText = (w, l) => `${l}×${w}″ (length × width)`;

/* 29 Oct (AR go): Custom Size before the Arrives note (checkout and the order show a line's notes in key order):
   hidden notes first, then Custom Size (any spelling), then the rest as they were */
export function sizeFirst(p) {
  const ks = Object.keys(p || {});
  const cs = (k) => /^custom size$/i.test(k);
  if (!ks.some(cs)) return p;
  const out = {};
  for (const k of ks) if (k.startsWith('_')) out[k] = p[k];
  for (const k of ks) if (cs(k)) out[k] = p[k];
  for (const k of ks) if (!k.startsWith('_') && !cs(k)) out[k] = p[k];
  return out;
}

/**
 * Add several items in one call, so a bed lands whole or not at all.
 * items: [{ id, quantity, properties }]
 */
export async function addItems(items, { read = true, base = null, notes = null } = {}) {
  const added = await call('cart/add.js', notes ? { items, attributes: notes } : { items });
  const lines = added.items || [added];
  /* 25 Oct (speed): when the cart page opens next, it shows the cart itself; no read here */
  if (!read) return { items: null, added: lines };
  if (!base) return announce(await get(), { added: lines });
  /* 25 Oct (speed): the cart from before the Add plus Shopify's answer (each added line with its new quantity and
     price), so the page answers without a second call. The true cart is read behind it and told to the page only
     if it differs. */
  const cart = merged(base, lines);
  announce(cart, { added: lines });
  get()
    .then((fresh) => {
      if (sig(fresh) !== sig(cart)) announce(fresh);
      else here = Promise.resolve(fresh);
    })
    .catch(() => {});
  return cart;
}

function merged(base, lines) {
  const items = base.items.map((l) => ({ ...l }));
  for (const a of lines) {
    const line = { ...a, price: a.final_price, properties: a.properties || {} };
    const i = items.findIndex((l) => l.key === a.key);
    if (i >= 0) items[i] = line;
    else items.unshift(line);
  }
  return {
    ...base,
    items,
    item_count: items.reduce((n, l) => n + l.quantity, 0),
    total_price: items.reduce((n, l) => n + (l.final_line_price || 0), 0),
  };
}

/**
 * Product-page add. 25 Oct (speed): starts from the cart this page holds (current(): printed with the page, kept
 * up to date by every change here, read again on coming back from another tab), so bed keys stay right with no
 * read before the Add. A mattress starts a new bed (the added sheet then offers Replace / Keep both);
 * an accessory joins the first bed of the same standard size that lacks one, else "Extras";
 * with anyBed (a pillow, not sized to a bed) it joins the first bed that lacks one.
 * size: { w, l } of the standard variant (the one a custom size is cut from).
 * also: more variant ids added in the same call to the same bed (the mattress page's "Add both": its topper).
 */
export async function addFromProductPage({ variantId, productId, size, custom, anyBed = false, also = [], go = false }) {
  const cart = await current().catch(() => get());
  const mattress = mattressIds().includes(Number(productId));
  const properties = {};
  let replaceable = null;
  if (mattress) {
    const existing = cart.items.find(isMattress);
    properties._bed = newBedKey(cart);
    /* 29 Oct (AR go): Custom Size before Arrives */
    if (custom) properties['Custom Size'] = customSizeText(custom.w, custom.l);
    Object.assign(properties, mattressNotes());
    if (existing) replaceable = { line: existing, bed: bedOf(existing) };
  } else if (size) {
    const bed = beds(cart).find(
      (b) =>
        b.key !== 'extras' &&
        b.lines.some((l) => isMattress(l) && sameStandard(l, size)) &&
        !b.lines.some((l) => Number(l.product_id) === Number(productId))
    );
    if (bed) properties._bed = bed.key;
  } else if (anyBed) {
    // Not sized to a bed (a pillow): the first bed that has a mattress and none of these yet.
    const bed = beds(cart).find(
      (b) => b.key !== 'extras' && b.lines.some(isMattress) && !b.lines.some((l) => Number(l.product_id) === Number(productId))
    );
    if (bed) properties._bed = bed.key;
  }
  if (custom) properties['Custom Size'] = customSizeText(custom.w, custom.l);
  const more = also.map((id) => ({ id, quantity: 1, properties: properties._bed ? { _bed: properties._bed } : {} }));
  /* go: the cart opens next (a mattress), so the cart is not read again after the add */
  const result = await addItems([{ id: variantId, quantity: 1, properties }, ...more], { read: !go, base: cart, notes: orderNotes(true) });
  return { cart: go ? null : result, replaceable, bed: properties._bed || null };
}

function sameStandard(line, size) {
  const t = line.variant_title || '';
  const m = /(\d+)\s*["″]?\s*[x×]\s*(\d+)/.exec(t);
  return !!m && Number(m[1]) === size.w && Number(m[2]) === size.l;
}

/**
 * Replace: the new mattress takes the old one's bed; the old mattress goes.
 * The same mattress and size twice ("Keep one"): the new line simply goes, the old bed stays as it was.
 * Otherwise the old line goes first, then the new line (found again: its key changes with its
 * properties) takes the old bed's _bed / _bed_label. Doing it in this order means the two lines can
 * never become identical and merge (which used to remove both).
 */
export async function replaceMattress({ newLineKey, oldLine }) {
  const cart = await get();
  const line = cart.items.find((l) => l.key === newLineKey);
  if (!line) return cart;
  if (Number(line.variant_id) === Number(oldLine.variant_id)) {
    return announce(await call('cart/change.js', { id: newLineKey, quantity: 0 }));
  }
  const props = { ...(line.properties || {}), _bed: bedOf(oldLine) };
  const label = oldLine.properties && oldLine.properties._bed_label;
  if (label) props._bed_label = String(label).trim();
  else delete props._bed_label;
  const after = await call('cart/change.js', { id: oldLine.key, quantity: 0 });
  const again = after.items.find((l) => l.key === newLineKey) || after.items.find((l) => Number(l.variant_id) === Number(line.variant_id) && bedOf(l) === bedOf(line));
  if (!again) return announce(after);
  const updated = await call('cart/change.js', { id: again.key, quantity: again.quantity, properties: props });
  return announce(updated);
}

/** Remove every line of one bed. Returns the removed lines (for Undo). */
export async function removeBed(key) {
  const cart = await get();
  const lines = cart.items.filter((l) => (bedOf(l) || 'extras') === key);
  const updates = Object.fromEntries(lines.map((l) => [l.key, 0]));
  const updated = await call('cart/update.js', { updates });
  announce(updated, { removed: lines });
  return lines;
}

/** Put removed lines back (Undo). */
export async function restore(lines) {
  return addItems(lines.map((l) => ({ id: l.variant_id, quantity: l.quantity, properties: sizeFirst(l.properties || {}) })));
}

/**
 * Build your bed: replace this bed's builder lines (and any other mattress in the same bed) with `items`.
 * Never two mattresses in one bed.
 */
export async function updateBed(key, items) {
  const cart = await get();
  const drop = cart.items.filter((l) => bedOf(l) === key && ((l.properties && l.properties._from_builder) || isMattress(l)));
  if (drop.length) await call('cart/update.js', { updates: Object.fromEntries(drop.map((l) => [l.key, 0])) });
  return addItems(items);
}

/** One line to a new quantity (0 takes it out). */
export async function setQty(key, quantity) {
  return announce(await call('cart/change.js', { id: key, quantity }));
}

/** Read the cart and tell the page (header count, In your cart buttons). */
export async function refresh() {
  return announce(await get());
}

/** Count of items, for the header badge. */
export const count = (cart) => cart.item_count;
